import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

const SelectContainer = styled.div`
    border-radius: 2px;
    display: inline-block;
    position: relative;
    vertical-align: middle;
    width: 100%;

    &:after {
        border: solid #d8d8d8;
        border-width: 0 2px 2px 0;
        content: '';
        display: inline-block;
        padding: 4px;
        pointer-events: none;
        position: absolute;
        right: 15px;
        top: 22px;
        transform: translateY(-50%) rotate(45deg);
    }
`;

const SearchInput = styled.input`
    width: 100%;
    height: 36px;
    border: 1px solid #d8d8d8;
    border-radius: 2px;
    padding: 0 13px;
    font-family: 'lato-regular', sans-serif;
    font-size: 13px;
    outline: none;
    box-sizing: border-box;
`;

const DropdownList = styled.ul`
    position: absolute;
    width: 100%;
    max-height: 200px;
    border: 1px solid #d8d8d8;
    background: white;
    padding: 0;
    list-style: none;
    overflow-y: auto;
    z-index: 10;
    margin-top: 5px;
    border-radius: 5px;
    box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;
`;

const DropdownItem = styled.li`
    padding: 8px 13px;
    cursor: pointer;
    &:hover {
        background: #f0f0f0;
    }
`;

const StyledErrorWrapper = styled.span`
    color: #f00;
    display: inline-block;
    font-size: 12px;
    margin: 8px 0;
`;

const Select = ({
    id,
    wrapperClass,
    className,
    data,
    label,
    valueField,
    labelField,
    handleChange,
    hideErrors,
    disabled,
    meta,
    loader,
    selected,
    handleReset,
    handleSearchEnd,
}) => {
    const selectedRef = useRef(false);
    const searchValueRef = useRef('');
    const [searchValue, setSearchValue] = useState('');
    const [filteredData, setFilteredData] = useState(data);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const isInputActive = useRef(false);

    useEffect(() => {
        const selectedItem = data.find(item => item[valueField] === selected);

        if ((data.length === 0 || !selectedItem) && !isInputActive.current) {
            setSearchValue('');
            searchValueRef.current = '';
        } else if (!selectedRef.current && selectedItem) {
            setSearchValue(selectedItem[labelField]);
            selectedRef.current = true;
        }
    }, [data, selected, valueField, labelField]);

    // Filter data based on search input
    useEffect(() => {
        if (searchValue.trim()) {
            const lowercasedFilter = searchValue.toLowerCase();
            const filtered = data.filter(item => item[labelField].toLowerCase().includes(lowercasedFilter));
            if (JSON.stringify(filtered) !== JSON.stringify(filteredData)) {
                setFilteredData(filtered);
            }
        } else if (JSON.stringify(data) !== JSON.stringify(filteredData)) {
            setFilteredData(data);
        }
    }, [searchValue, data, labelField, filteredData]);

    const handleSearchChange = event => {
        handleSearchEnd(event.target.value);
        setSearchValue(event.target.value);
        if (event.target.value === '') {
            handleReset();
        }
    };

    const handleSelect = (value, selectedItem) => {
        setSearchValue(selectedItem[labelField]);
        setIsDropdownOpen(false);
        isInputActive.current = false;

        if (handleChange) {
            handleChange(value, selectedItem);
        }
    };

    const handleInputFocus = () => {
        setIsDropdownOpen(true);
        isInputActive.current = true;
    };

    const handleBlur = () => {
        setTimeout(() => setIsDropdownOpen(false), 150);
        isInputActive.current = false;
        if (searchValue === '') {
            if (typeof handleReset === 'function') {
                handleReset();
            }
            if (searchValue !== searchValueRef.current) {
                handleChange('', {});
                searchValueRef.current = '';
            }
        } else {
            searchValueRef.current = searchValue;
        }
    };

    return (
        <SelectContainer className={wrapperClass}>
            <SearchInput
                id={id}
                className={className}
                type="text"
                value={searchValue}
                onChange={handleSearchChange}
                onFocus={handleInputFocus}
                onBlur={handleBlur}
                placeholder={label || 'Select...'}
                disabled={disabled}
            />
            {isDropdownOpen && (
                <DropdownList onMouseDown={event => event.preventDefault()}>
                    {filteredData.map(item => (
                        <DropdownItem key={item[valueField]} onClick={() => handleSelect(item[valueField], item)}>
                            {item[labelField]}
                        </DropdownItem>
                    ))}
                    {filteredData.length === 0 && <DropdownItem>No options found</DropdownItem>}
                </DropdownList>
            )}
            {!hideErrors && meta?.touched && meta.error && <StyledErrorWrapper>{meta.error}</StyledErrorWrapper>}
        </SelectContainer>
    );
};

Select.propTypes = {
    id: PropTypes.string,
    wrapperClass: PropTypes.string,
    className: PropTypes.string,
    data: PropTypes.array.isRequired,
    label: PropTypes.string,
    valueField: PropTypes.string.isRequired,
    labelField: PropTypes.string.isRequired,
    handleChange: PropTypes.func,
    defaultSelected: PropTypes.string,
    hideErrors: PropTypes.bool,
    disabled: PropTypes.bool,
    meta: PropTypes.object,
    loader: PropTypes.bool,
    selected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    handleSearchEnd: PropTypes.func,
    handleReset: PropTypes.func,
};

Select.defaultProps = {
    data: [],
    wrapperClass: '',
    className: '',
    hideErrors: false,
    disabled: false,
    loader: false,
    // eslint-disable-next-line no-empty-function
    handleSearchEnd: () => {},
    // eslint-disable-next-line no-empty-function
    handleReset: () => {},
};

export default Select;
